import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

function getPageRange(currentPage: number, totalPages: number): { startPage: number; endPage: number } {
  if (currentPage <= 3) return { startPage: 1, endPage: 3 };
  if (currentPage >= totalPages - 4) return { startPage: totalPages - 4, endPage: totalPages - 2 };
  return { startPage: currentPage - 1, endPage: currentPage + 1 };
}

function buildMultiPageNumbers(
  currentPage: number,
  totalPages: number,
  createPageButton: (i: number) => React.ReactElement,
  createEllipsis: (key: string) => React.ReactElement,
): React.ReactElement[] {
  const pages: React.ReactElement[] = [];
  pages.push(createPageButton(0));
  const { startPage, endPage } = getPageRange(currentPage, totalPages);
  if (startPage > 1) pages.push(createEllipsis('start'));
  for (let i = startPage; i <= endPage; i++) {
    if (i > 0 && i < totalPages - 1) pages.push(createPageButton(i));
  }
  if (endPage < totalPages - 2) pages.push(createEllipsis('end'));
  if (totalPages > 1) pages.push(createPageButton(totalPages - 1));
  return pages;
}

export default function TablePagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}: Readonly<PaginationProps>) {
  // Reusable function to create page buttons
  const createPageButton = (pageIndex: number) => (
    <button
      key={pageIndex}
      onClick={() => onPageChange(pageIndex)}
      disabled={disabled}
      className={`px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
        currentPage === pageIndex ? 'bg-active text-slate-600' : 'bg-transparent text-slate-400 hover:text-slate-600'
      }`}
    >
      {pageIndex + 1}
    </button>
  );

  // Reusable function to create ellipsis
  const createEllipsis = (key: string) => (
    <span key={key} className="px-3 py-1 text-slate-400">
      ...
    </span>
  );

  const renderPageNumbers = () => {
    if (totalPages <= 7) {
      const pages = [];
      for (let i = 0; i < totalPages; i++) {
        pages.push(createPageButton(i));
      }
      return pages;
    }
    return buildMultiPageNumbers(currentPage, totalPages, createPageButton, createEllipsis);
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="mt-4 p-4">
      <div className="flex justify-between">
        <button
          aria-disabled={disabled || currentPage === 0}
          onClick={() => {
            if (!(disabled || currentPage === 0)) onPageChange(currentPage - 1);
          }}
          className={`flex flex-row rounded-lg items-center align-middle border py-2 px-[15px] text-slate-600 text-sm 
    ${disabled || currentPage === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-100'}`}
        >
          <ArrowLeft size={14} className="mr-1" />
          Previous
        </button>

        <div className="flex items-center gap-1 text-sm">{renderPageNumbers()}</div>

        <button
          aria-disabled={disabled || currentPage === totalPages - 1}
          onClick={() => {
            if (!(disabled || currentPage === totalPages - 1)) onPageChange(currentPage + 1);
          }}
          className={`flex flex-row rounded-lg items-center align-middle border py-2 px-[15px] text-slate-600 text-sm
    ${disabled || currentPage === totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-100'}`}
        >
          Next
          <ArrowRight size={14} className="ml-1" />
        </button>
      </div>
    </div>
  );
}
